import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { adminFetch } from '../hooks/useAdminFetch'

interface TenantItem {
  id: string
  name: string
  plan: string
  created_at: string | null
  last_activity: string | null
  uploads_30d: number
  status: 'active' | 'trial_expiring' | 'inactive'
}

interface TenantsResponse {
  items: TenantItem[]
  total: number
  limit: number
  offset: number
}

const PLAN_COLORS: Record<string, string> = {
  trial: 'bg-yellow-500/15 text-yellow-300',
  starter: 'bg-blue-500/15 text-blue-300',
  growth: 'bg-purple-500/15 text-purple-300',
  enterprise: 'bg-green-500/15 text-green-300',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-500/15 text-green-300',
  trial_expiring: 'bg-orange-500/15 text-orange-300',
  inactive: 'bg-gray-500/15 text-gray-400',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Attivo',
  trial_expiring: 'Trial in scadenza',
  inactive: 'Inattivo',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function Badge({ value, colorMap }: { value: string; colorMap: Record<string, string> }) {
  const cls = colorMap[value] ?? 'bg-gray-500/15 text-gray-400'
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${cls}`}>
      {value}
    </span>
  )
}

const LIMIT = 50

export function AdminTenants() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [orderBy, setOrderBy] = useState('created_at')
  const [offset, setOffset] = useState(0)

  const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset), order_by: orderBy })
  if (search) params.set('search', search)
  if (planFilter) params.set('plan', planFilter)
  if (statusFilter) params.set('status', statusFilter)

  const { data, isLoading, error } = useQuery<TenantsResponse>({
    queryKey: ['admin', 'tenants', search, planFilter, statusFilter, orderBy, offset],
    queryFn: () => adminFetch<TenantsResponse>(`/api/v1/admin/tenants?${params.toString()}`),
  })

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value)
    setOffset(0)
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--km-bg)',
    borderColor: 'var(--km-border)',
    color: 'var(--km-text-primary)',
  }

  const selectStyle: React.CSSProperties = {
    background: 'var(--km-bg)',
    borderColor: 'var(--km-border)',
    color: 'var(--km-text-secondary)',
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
          Tenant
        </h2>
        {data && (
          <div className="text-sm" style={{ color: 'var(--km-text-muted)' }}>
            {data.total} tenant totali
          </div>
        )}
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Cerca per nome..."
          value={search}
          onChange={handleSearch}
          className="px-3 py-2 rounded-lg text-sm border outline-none flex-1 min-w-[200px]"
          style={inputStyle}
        />

        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-sm border outline-none"
          style={selectStyle}
        >
          <option value="">Tutti i piani</option>
          <option value="trial">Trial</option>
          <option value="starter">Starter</option>
          <option value="growth">Growth</option>
          <option value="enterprise">Enterprise</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-sm border outline-none"
          style={selectStyle}
        >
          <option value="">Tutti gli stati</option>
          <option value="active">Attivi</option>
          <option value="trial_expiring">Trial in scadenza</option>
          <option value="inactive">Inattivi</option>
        </select>

        <select
          value={orderBy}
          onChange={(e) => { setOrderBy(e.target.value); setOffset(0) }}
          className="px-3 py-2 rounded-lg text-sm border outline-none"
          style={selectStyle}
        >
          <option value="created_at">Data creazione</option>
          <option value="plan">Piano</option>
          <option value="activity">Ultima attività</option>
        </select>
      </div>

      {/* Tabella */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--km-border)' }}
      >
        {isLoading && (
          <div className="p-6 text-sm text-center" style={{ color: 'var(--km-text-muted)' }}>
            Caricamento...
          </div>
        )}

        {error && (
          <div className="p-6 text-sm text-center" style={{ color: 'var(--km-error)' }}>
            Errore nel caricamento dei tenant.
          </div>
        )}

        {data && !isLoading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-wider border-b"
                  style={{
                    background: 'var(--km-surface)',
                    borderColor: 'var(--km-border)',
                    color: 'var(--km-text-muted)',
                  }}
                >
                  <th className="text-left px-4 py-3 font-medium">Nome azienda</th>
                  <th className="text-left px-4 py-3 font-medium">Piano</th>
                  <th className="text-left px-4 py-3 font-medium">Creato il</th>
                  <th className="text-left px-4 py-3 font-medium">Ultima attività</th>
                  <th className="text-center px-4 py-3 font-medium">Upload 30gg</th>
                  <th className="text-left px-4 py-3 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-sm"
                      style={{ color: 'var(--km-text-muted)' }}
                    >
                      Nessun tenant trovato
                    </td>
                  </tr>
                ) : (
                  data.items.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/admin/tenants/${t.id}`)}
                      className="border-b transition-colors hover:bg-white/[0.04] cursor-pointer"
                      style={{ borderColor: 'var(--km-border)' }}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium" style={{ color: 'var(--km-text-primary)' }}>
                          {t.name}
                        </div>
                        <div className="text-xs mt-0.5 font-mono" style={{ color: 'var(--km-text-muted)' }}>
                          {t.id.slice(0, 8)}…
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={t.plan} colorMap={PLAN_COLORS} />
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--km-text-secondary)' }}>
                        {formatDate(t.created_at)}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--km-text-secondary)' }}>
                        {formatDate(t.last_activity)}
                      </td>
                      <td className="px-4 py-3 text-center" style={{ color: 'var(--km-text-secondary)' }}>
                        {t.uploads_30d}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status] ?? ''}`}
                        >
                          {STATUS_LABELS[t.status] ?? t.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginazione */}
      {data && data.total > LIMIT && (
        <div className="flex items-center gap-3 justify-end">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            className="px-3 py-1.5 rounded-lg text-xs border transition-colors disabled:opacity-40"
            style={{
              background: 'var(--km-surface)',
              borderColor: 'var(--km-border)',
              color: 'var(--km-text-secondary)',
            }}
          >
            Precedente
          </button>
          <span className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
            {offset + 1}–{Math.min(offset + LIMIT, data.total)} di {data.total}
          </span>
          <button
            disabled={offset + LIMIT >= data.total}
            onClick={() => setOffset(offset + LIMIT)}
            className="px-3 py-1.5 rounded-lg text-xs border transition-colors disabled:opacity-40"
            style={{
              background: 'var(--km-surface)',
              borderColor: 'var(--km-border)',
              color: 'var(--km-text-secondary)',
            }}
          >
            Successiva
          </button>
        </div>
      )}
    </div>
  )
}
